import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { FileText, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { parseDSL } from '../utils/dslParser';
import { generateDSL } from '../utils/dslGenerator';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-sql';
import 'prismjs/themes/prism-tomorrow.css';

export const DSLEditor = ({ tables, relationships, onUpdate, className }) => {
  const [dslText, setDslText] = useState('');
  const [parseError, setParseError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef(null);

  // Generate DSL from current canvas state
  useEffect(() => {
    if (!isEditing) {
      const generated = generateDSL(tables, relationships);
      setDslText(generated);
    }
  }, [tables, relationships, isEditing]);

  const handleDSLChange = (newValue) => {
    setDslText(newValue);
    setIsEditing(true);
    setParseError('');
  };

  const handleApplyDSL = () => {
    try {
      const parsed = parseDSL(dslText);
      
      if (parsed.tables.length === 0) {
        setParseError('Tidak ada tabel yang ditemukan');
        toast.error('Tidak ada tabel yang ditemukan dalam syntax');
        return;
      }

      onUpdate(parsed);
      setParseError('');
      setIsEditing(false);
      toast.success(`Berhasil update ${parsed.tables.length} tabel dan ${parsed.relationships.length} relasi`);
    } catch (error) {
      console.error('DSL Parse Error:', error);
      setParseError(error.message);
      toast.error('Syntax error: ' + error.message);
    }
  };

  const handleRegenerate = () => {
    const generated = generateDSL(tables, relationships);
    setDslText(generated);
    setIsEditing(false);
    setParseError('');
    toast.success('DSL di-regenerate dari canvas');
  };
  
  // Custom DSL syntax highlighting
  const highlightDSL = (code) => {
    // Custom highlighting for DSL syntax
    return code
      .split('\n')
      .map((line) => {
        const trimmed = line.trim();
        
        // Table declaration (e.g., "users {")
        if (trimmed.match(/^\w+\s*\{/)) {
          return `<span class="token keyword">${line}</span>`;
        }
        // Closing brace
        else if (trimmed === '}') {
          return `<span class="token punctuation">${line}</span>`;
        }
        // Relationship (e.g., "user_id bigint > users.id")
        else if (trimmed.includes('>')) {
          return line.replace(
            /(\w+)\s+(\w+(?:\(\d+\))?)\s+(>)\s+(\w+)\.(\w+)/,
            '<span class="token property">$1</span> <span class="token builtin">$2</span> <span class="token operator">$3</span> <span class="token class-name">$4</span>.<span class="token property">$5</span>'
          );
        }
        // Column definition with keywords
        else if (trimmed.match(/^\w+\s+\w+/)) {
          return line.replace(
            /(\w+)\s+(\w+(?:\(\d+\))?)\s*(pk|increments|unique|def\([^)]+\))?/,
            '<span class="token property">$1</span> <span class="token builtin">$2</span> <span class="token keyword">$3</span>'
          );
        }
        
        return line;
      })
      .join('\n');
  };

  return (
    <div className={`flex flex-col h-full bg-card border-r border-border ${className}`}>
      {/* Header */}
      <div className="p-3 border-b border-border flex items-center justify-between bg-accent/30">
        <div className="flex items-center space-x-2">
          <FileText className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <span className="font-bold text-sm" style={{fontFamily: 'Space Grotesk'}}>DSL Editor</span>
          {!parseError && dslText.trim() && (
            <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-400" />
          )}
        </div>
        <div className="flex items-center space-x-1">
          <Button
            data-testid="regenerate-dsl-btn"
            variant="ghost"
            size="sm"
            onClick={handleRegenerate}
            className="h-7 px-2"
            title="Regenerate dari canvas"
          >
            <RefreshCw className="w-3 h-3" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      {/* Editor area with syntax highlighting */}
      <div className="flex-1 overflow-auto bg-[#1e1e1e] dark:bg-[#1e1e1e]" style={{ minHeight: '400px' }}>
        <Editor
          value={dslText}
          onValueChange={handleDSLChange}
          highlight={(code) => highlightDSL(code)}
          padding={28}
          data-testid="dsl-editor-textarea"
          placeholder="Ketik syntax DSL di sini...

Contoh:
users {
  id bigint pk increments
  name varchar(100)
  email varchar(100)
}

products {
  id bigint pk increments
  user_id bigint > users.id
  name varchar(200)
}"
          style={{
            fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
            fontSize: 14,
            lineHeight: '1.8',
            minHeight: '400px',
            backgroundColor: 'transparent',
            color: '#d4d4d4',
            outline: 'none'
          }}
          textareaClassName="focus:outline-none"
        />
      </div>

      {/* Parse error */}
      {parseError && (
        <div className="px-3 py-2 bg-destructive/10 border-t border-destructive/20" data-testid="dsl-error">
          <div className="flex items-start space-x-2 text-xs text-destructive">
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <span>{parseError}</span>
          </div>
        </div>
      )}

      {/* Bottom controls */}
      <div className="p-3 border-t border-border flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {isEditing ? (
            <span className="text-amber-600 dark:text-amber-500">• Belum disimpan</span>
          ) : (
            <span className="text-green-600 dark:text-green-500">• Tersinkron</span>
          )}
        </div>
        <Button
          data-testid="apply-dsl-btn"
          size="sm"
          onClick={handleApplyDSL}
          disabled={!isEditing}
          className="h-8 hover:-translate-y-[1px] transition-transform duration-200"
        >
          Apply Changes
        </Button>
      </div>

      {/* Help section */}
      <div className="px-3 pb-3">
        <div className="text-[10px] text-muted-foreground space-y-1 bg-accent/30 p-2 rounded">
          <p className="font-semibold mb-1">Syntax Format:</p>
          <div className="space-y-0.5 ml-2">
            <div><span className="text-blue-600 dark:text-blue-400 font-semibold">tableName</span> <span className="text-gray-500">&#123;</span></div>
            <div className="ml-2">
              <span className="text-purple-600 dark:text-purple-400">columnName</span>{' '}
              <span className="text-green-600 dark:text-green-400">dataType</span>{' '}
              <span className="text-orange-500">pk increments</span>
            </div>
            <div className="ml-2">
              <span className="text-purple-600 dark:text-purple-400">fk_col</span>{' '}
              <span className="text-green-600 dark:text-green-400">bigint</span>{' '}
              <span className="text-red-500 font-bold">&gt;</span>{' '}
              <span className="text-blue-600 dark:text-blue-400">table</span>.<span className="text-purple-600 dark:text-purple-400">col</span>
            </div>
            <div><span className="text-gray-500">&#125;</span></div>
          </div>
          <p className="mt-2 text-[9px] opacity-75">
            <strong>Keywords:</strong> pk, increments, unique, def(value)
          </p>
        </div>
      </div>
    </div>
  );
};
